import { filter, map, Observable, pipe, UnaryFunction } from 'rxjs';
/**
 * Checks if a value is a valid enum value
 * @param value - The value to check if it is a valid enum value
 * @param enumObject - The enum object to check against
 * @returns true if the value is a valid enum value, false otherwise
 */
export function isEnumValue<TEnum extends Record<string, string>>(
  value: string,
  enumObject: TEnum,
): value is TEnum[keyof TEnum] {
  return Object.values(enumObject).includes(value);
}

/**
 * Filters an observable to only include valid enum values
 * @param enumObject - The enum object to check against
 * @returns an observable of valid enum values
 */
export function rxFilterIsEnumValue<TEnum extends Record<string, string>>(
  enumObject: TEnum,
): UnaryFunction<
  Observable<string | null | undefined>,
  Observable<TEnum[keyof TEnum]>
> {
  return pipe(
    filter(
      (value): value is TEnum[keyof TEnum] =>
        value != null && isEnumValue(value, enumObject),
    ),
  );
}

/**
 * Maps a value to an enum value with and falls back to a default value if the value is not a valid enum value
 * @param value - The value to map
 * @param enumObject - The enum object to map to
 * @param fallbackValue - The fallback value to use if the value is not a valid enum value
 * @returns the mapped enum value or the fallback value
 */
export function mapToEnumWithFallback<TEnum extends Record<string, string>>(
  value: string | null | undefined,
  enumObject: TEnum,
  fallbackValue: TEnum[keyof TEnum],
): TEnum[keyof TEnum] {
  if (value && isEnumValue(value, enumObject)) {
    return value;
  }
  return fallbackValue;
}

/**
 * Maps an observable to an enum value with a fallback value
 * @param enumObject - The enum object to map to
 * @param fallbackValue - The fallback value to use if the value is not a valid enum value
 * @returns an observable of mapped enum values
 */
export function rxMapToEnumWithFallback<TEnum extends Record<string, string>>(
  enumObject: TEnum,
  fallbackValue: TEnum[keyof TEnum],
): UnaryFunction<
  Observable<string | null | undefined>,
  Observable<TEnum[keyof TEnum]>
> {
  return pipe(
    map((value) => mapToEnumWithFallback(value, enumObject, fallbackValue)),
  );
}
